import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button as TamaguiButton, XStack, YStack } from "tamagui";
import AuthScaffold, { AuthChoiceCard, AuthInfoPanel, AuthStepDots } from "@/components/AuthScaffold";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { finishOnboardingProfile } from "@/lib/onboardingProfile";
import { trackKindredProductEvent } from "@/lib/productAnalytics";
import { consumeWebPostAuthRedirect } from "@/lib/postAuthRedirect";
import { toast } from "sonner";
import {
  DEFAULT_MEMBER_COUNTRY_CODE,
  getMemberCountryPickerOptions,
  getMemberPostalCodeExample,
  getMemberPostalCodeLabel,
  getMemberPostalCodeValidationMessage,
  normalizeMemberCountryCode,
  normalizeMemberPostalCode,
  normalizeUsStateOrTerritoryCode,
} from "../../shared/location";
import { AppButton, AppFieldGroup, AppInput, AppSegmentedControl, AppText, kindredFontFamilies } from "../../ui-foundation";

const interestOptions = [
  { emoji: "🏕️", title: "Activities", value: "adventures", desc: "Days out, festivals, camping trips" },
  { emoji: "👥", title: "Community", value: "community", desc: "Channels, chat rooms, and messages" },
  { emoji: "📋", title: "Practical support", value: "support", desc: "Helpful information and guides" },
  { emoji: "✨", title: "All of it", value: "all", desc: "Everything Kindred offers" },
];

const stepMeta = {
  1: {
    eyebrow: "Step 1 of 3",
    title: "Where are you based?",
    description: "We’ll use your local postal code to help surface nearby families, adventures, and useful local information.",
  },
  2: {
    eyebrow: "Step 2 of 3",
    title: "Tell us about your family",
    description: "Birth years help us shape the experience without asking for names or anything intrusive.",
  },
  3: {
    eyebrow: "Step 3 of 3",
    title: "What are you looking for first?",
    description: "Choose the parts of Kindred you most want to lean on right now. You can change this later.",
  },
} as const;

const Onboarding = () => {
  const navigate = useNavigate();
  const { user, refreshProfile } = useAuth();
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [countryCode, setCountryCode] = useState(DEFAULT_MEMBER_COUNTRY_CODE);
  const [postcode, setPostcode] = useState("");
  const [supportUsState, setSupportUsState] = useState("");
  const [children, setChildren] = useState<number[]>([]);
  const [selectedInterests, setSelectedInterests] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [postcodeError, setPostcodeError] = useState("");
  const [usStateError, setUsStateError] = useState("");

  useEffect(() => {
    if (!user) {
      return;
    }

    let active = true;
    void supabase
      .from("my_private_profile")
      .select("postcode, country_code, support_us_state")
      .maybeSingle()
      .then(({ data, error }) => {
        if (!active || error || !data) {
          return;
        }

        setPostcode(data.postcode ?? "");
        setCountryCode(normalizeMemberCountryCode(data.country_code));
        setSupportUsState(normalizeUsStateOrTerritoryCode(data.support_us_state) ?? data.support_us_state ?? "");
      });

    return () => {
      active = false;
    };
  }, [user?.id]);

  const handleContinueStep1 = async () => {
    if (!user) return;
    const postcodeLabel = getMemberPostalCodeLabel(countryCode).toLowerCase();
    const normalizedPostcode = normalizeMemberPostalCode(countryCode, postcode);
    if (!postcode.trim()) {
      setPostcodeError(`Please enter your ${postcodeLabel}`);
      return;
    }
    if (!normalizedPostcode) {
      setPostcodeError(getMemberPostalCodeValidationMessage(countryCode));
      return;
    }

    const normalizedUsState = countryCode === "US" ? normalizeUsStateOrTerritoryCode(supportUsState) : null;
    if (countryCode === "US" && !normalizedUsState) {
      setUsStateError("Please add your U.S. state or territory.");
      return;
    }

    setPostcodeError("");
    setUsStateError("");
    setLoading(true);
    const { error } = await supabase
      .from("profiles")
      .update({
        postcode: normalizedPostcode,
        country_code: countryCode,
        support_us_state: countryCode === "US" ? normalizedUsState : null,
      })
      .eq("id", user.id);
    setLoading(false);
    if (error) {
      toast.error(`Couldn't save your ${postcodeLabel}. Please try again.`);
      return;
    }
    await trackKindredProductEvent({
      userId: user.id,
      eventName: "onboarding_step_completed",
      screenName: "onboarding",
      entityType: "onboarding_step",
      entityId: "1",
    });
    setStep(2);
  };

  const addChild = () => {
    if (children.length < 6) {
      setChildren([...children, new Date().getFullYear()]);
    }
  };

  const updateChild = (index: number, year: number) => {
    const updated = [...children];
    updated[index] = year;
    setChildren(updated);
  };

  const removeChild = (index: number) => {
    setChildren(children.filter((_, i) => i !== index));
  };

  const handleContinueStep2 = async () => {
    if (!user) return;
    setLoading(true);
    try {
      await supabase.from("children").delete().eq("user_id", user.id);
      if (children.length > 0) {
        const childRows = children.map((birth_year) => ({
          user_id: user.id,
          birth_year,
        }));
        await supabase.from("children").insert(childRows);
      }
    } catch {
      toast.error("Couldn't save children info. Please try again.");
      setLoading(false);
      return;
    }
    setLoading(false);
    await trackKindredProductEvent({
      userId: user.id,
      eventName: "onboarding_step_completed",
      screenName: "onboarding",
      entityType: "onboarding_step",
      entityId: "2",
      metadata: { childCount: children.length },
    });
    setStep(3);
  };

  const toggleInterest = (value: string) => {
    setSelectedInterests((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
    );
  };

  const handleFinish = async () => {
    if (!user) return;
    setLoading(true);
    const { error } = await finishOnboardingProfile(user.id, selectedInterests);
    setLoading(false);
    if (error) {
      toast.error("Something went wrong. Please try again.");
      return;
    }
    await trackKindredProductEvent({
      userId: user.id,
      eventName: "onboarding_step_completed",
      screenName: "onboarding",
      entityType: "onboarding_step",
      entityId: "3",
      metadata: { selectedInterests },
    });
    await refreshProfile();
    toast.success("Welcome to Kindred! 🎉");
    navigate(consumeWebPostAuthRedirect());
  };

  const postcodeLabel = getMemberPostalCodeLabel(countryCode);
  const postcodeExample = getMemberPostalCodeExample(countryCode);

  return (
    <AuthScaffold
      eyebrow={stepMeta[step].eyebrow}
      title={stepMeta[step].title}
      description={stepMeta[step].description}
      sideTitle="This is the part where Kindred starts to feel local."
      sideDescription="A few details now mean less noise later. We keep it light, useful, and easy to edit once you are inside the app."
      highlights={[
        {
          title: "Small setup, better recommendations",
          description: "Location and interests shape the first things you see.",
        },
        {
          title: "No over-sharing required",
          description: "We ask for just enough to make the app useful.",
        },
        {
          title: "You can change this later",
          description: "Nothing here locks you into one version of family life.",
        },
      ]}
    >
      <AuthStepDots current={step} total={3} />

      {step === 1 ? (
        <YStack gap="$sm">
          <AppFieldGroup label="Country" helper="This keeps your location format right wherever you use Kindred.">
            <AppSegmentedControl
              options={getMemberCountryPickerOptions(countryCode).map((option) => ({ value: option.value, label: option.label }))}
              value={countryCode}
              onChange={(value) => {
                setCountryCode(normalizeMemberCountryCode(value));
                setPostcodeError("");
                setUsStateError("");
              }}
              wrapItems
            />
          </AppFieldGroup>
          <AppFieldGroup
            label={<label htmlFor="postcode">{postcodeLabel}</label>}
            helper={`Example: ${postcodeExample}`}
            error={postcodeError}
          >
            <AppInput
              id="postcode"
              value={postcode}
              onChangeText={(value) => {
                setPostcode(value);
                setPostcodeError("");
              }}
              placeholder={`e.g. ${postcodeExample}`}
              autoComplete="postal-code"
            />
          </AppFieldGroup>
          {countryCode === "US" ? (
            <AppFieldGroup
              label={<label htmlFor="us-state">State / territory</label>}
              helper="For U.S. support, Kindred uses this to route you to the right state source after the national explainer."
              error={usStateError}
            >
              <AppInput
                id="us-state"
                value={supportUsState}
                onChangeText={(value) => {
                  setSupportUsState(value);
                  setUsStateError("");
                }}
                placeholder="e.g. CA or California"
                autoComplete="address-level1"
              />
            </AppFieldGroup>
          ) : null}
          <AppButton type="button" onPress={handleContinueStep1} disabled={loading} width="100%">
            {loading ? "Saving..." : "Continue"}
          </AppButton>
        </YStack>
      ) : null}

      {step === 2 ? (
        <YStack gap="$sm">
          <AuthInfoPanel>
            <AppText color="$colorMuted" fontSize="$sm" lineHeight="$md">
              Add your children by birth year only. You can skip this for now if you prefer.
            </AppText>
          </AuthInfoPanel>

          {children.map((year, i) => (
            <XStack key={i} alignItems="center" gap="$sm">
              <AppInput
                type="number"
                min={2000}
                max={2026}
                value={String(year)}
                onChangeText={(value) => updateChild(i, parseInt(value, 10) || 2020)}
                placeholder="Birth year"
                flex={1}
              />
              <TamaguiButton
                unstyled
                type="button"
                onPress={() => removeChild(i)}
                width={44}
                height={44}
                alignItems="center"
                justifyContent="center"
                borderWidth={1}
                borderRadius="$pill"
                borderColor="$borderColor"
                backgroundColor="$cardBackground"
                pressStyle={{ opacity: 0.92 }}
              >
                <AppText color="$colorMuted" fontFamily={kindredFontFamilies.sansBold}>
                  ✕
                </AppText>
              </TamaguiButton>
            </XStack>
          ))}

          {children.length < 6 ? (
            <TamaguiButton unstyled type="button" onPress={addChild} alignSelf="flex-start" pressStyle={{ opacity: 0.8 }}>
              <AppText color="$primary" fontSize="$sm" lineHeight="$sm">
                + Add a child
              </AppText>
            </TamaguiButton>
          ) : null}

          <YStack gap="$xs">
            <AppButton type="button" onPress={handleContinueStep2} disabled={loading} width="100%">
              {loading ? "Saving..." : "Continue"}
            </AppButton>
            <AppButton type="button" tone="outline" onPress={() => setStep(3)} width="100%">
              Skip for now
            </AppButton>
          </YStack>
        </YStack>
      ) : null}

      {step === 3 ? (
        <YStack gap="$sm">
          <YStack gap="$xs">
            {interestOptions.map((opt) => (
              <AuthChoiceCard
                key={opt.value}
                selected={selectedInterests.includes(opt.value)}
                onPress={() => toggleInterest(opt.value)}
              >
                <XStack alignItems="flex-start" gap="$sm">
                  <AppText fontSize="$xxl" lineHeight="$xxl">
                    {opt.emoji}
                  </AppText>
                  <YStack flex={1} gap={4}>
                    <AppText fontFamily={kindredFontFamilies.sansBold} fontSize="$md" lineHeight="$md">
                      {opt.title}
                    </AppText>
                    <AppText color="$colorMuted" fontSize="$sm" lineHeight="$md">
                      {opt.desc}
                    </AppText>
                  </YStack>
                </XStack>
              </AuthChoiceCard>
            ))}
          </YStack>

          <AppButton type="button" onPress={handleFinish} disabled={loading} width="100%">
            {loading ? "Setting up..." : "Get started"}
          </AppButton>
        </YStack>
      ) : null}
    </AuthScaffold>
  );
};

export default Onboarding;
